import React, { useCallback, useMemo, useState } from 'react';
import {
  StyleSheet,
  Platform,
} from 'react-native';
import { showAlert } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ADMIN_COLORS } from '@/constants/admin';
import { useCreateBulletinV2Mutation, convertToFormData } from '@/store/api/admin/bulletinsApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { isNetworkError } from '@/utils/admin/errorHandling';
import { useCreateBulletinForm } from '@/components/admin/bulletins/hooks/useCreateBulletinForm';
import { useDatePicker } from '@/components/admin/events/CreateEvent/hooks/useDatePicker';
import { ActionButtons, DateTimePickerModal, PhotoUploadSection } from '@/components';
import { BulletinDateTimePickers } from '@/components/admin/bulletins/BulletinDateTimePickers';
import { 
  BulletinDetailsSection, 
  RecipientOptionsSection, 
  UrgencyOptionsSection, 
  PublishOptionsSection 
} from '@/components/admin/bulletins/CreateBulletin/components';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type CreateBulletinScreenProps = StackScreenProps<AdminStackParamList, 'CreateBulletin'>;

export const CreateBulletinScreen: React.FC<CreateBulletinScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const { showSuccess, showLoading, hideLoading, showWarning } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const [createBulletin, { isLoading }] = useCreateBulletinV2Mutation();

  // Custom hook for form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    showDiscardAlert,
  } = useCreateBulletinForm();

  // Custom hook for date picker logic
  const {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showPublishDatePicker,
    hideDatePickers,
    confirmDateSelection,
    showFallbackDatePicker,
    setDatePickerMode,
  } = useDatePicker();

  // Web-only anchor state for dropdown positioning (match Events implementation)
  const [webAnchor, setWebAnchor] = useState<AnchorRect | null>(null);

  // Memoized date selection handler to prevent unnecessary re-renders
  const handleDateSelection = useCallback((date: Date, type: 'start' | 'end' | 'publish') => {
    if (type === 'publish') {
      handleFieldChange('publishTime', date);
      // Also update publishDate as ISO string for API
      handleFieldChange('publishDate', date.toISOString());
    }
  }, [handleFieldChange]);

  // Date picker handler with fallback support
  const handlePublishTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showPublishDatePicker(formData.publishTime || new Date());
      return;
    }
    if (DateTimePicker) {
      showPublishDatePicker(formData.publishTime || new Date());
    } else {
      showFallbackDatePicker('Publish Time', formData.publishTime || new Date(), (date) => {
        handleDateSelection(date, 'publish');
      });
    }
  }, [DateTimePicker, formData.publishTime, showPublishDatePicker, showFallbackDatePicker, handleDateSelection]);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    const loadingId = showLoading('Creating bulletin...');
    try {
      // Convert form data to the format expected by the API
      const bulletinData = {
        title: formData.title,
        message: formData.message,
        propertyId: selectedPropertyId,
        publishType: formData.publishType,
        publishDate: formData.publishTime?.toISOString() || formData.publishDate,
        highlight: formData.highlight,
        sendEmail: formData.sendEmail,
        pinHomepage: formData.pinHomepage,
        recipientType: formData.recipientType,
        photos: formData.photos || [],
      };

      const formDataToSend = convertToFormData(bulletinData);
      const result = await createBulletin(formDataToSend).unwrap();
      
      hideLoading(loadingId);
      const successMessage = 
        formData.publishType === 'draft' 
          ? 'Bulletin saved as draft!' 
          : formData.publishType === 'scheduled' 
            ? 'Bulletin scheduled successfully!' 
            : 'Bulletin published successfully!';
      
      // Check for partial success (bulletin created but photos failed to upload)
      if (result.warning) {
        showWarning(`${successMessage} However, ${result.warning}`, {
          duration: 6000, // Longer duration for important warning
        });
      } else {
        showSuccess(successMessage);
      }
      navigation.goBack();
    } catch (error: any) {
      hideLoading(loadingId);
      console.error('Failed to create bulletin:', error);
      
      // Handle timeout errors specifically
      if (error?.status === 'TIMEOUT_ERROR' || error?.name === 'AbortError') {
        showAlert(
          'Request Timeout',
          'The bulletin creation is taking longer than expected. Please check if it was created successfully by refreshing the bulletins list.'
        );
        navigation.goBack();
      } else if (isNetworkError(error)) {
        showApiError(error, 'Network error. Please check your connection and try again.');
      } else {
        showApiError(error, 'Failed to create bulletin');
      }
    }
  }, [
    validateForm,
    selectedPropertyId,
    formData,
    createBulletin,
    showSuccess,
    showWarning,
    showLoading,
    hideLoading,
    showApiError,
    navigation,
  ]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (Platform.OS === 'web') {
      // DesktopFormPage handles web discard confirmation; just navigate back
      navigation.goBack();
      return;
    }
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation]);

  // Date picker confirmation handler
  const handleDatePickerConfirm = useCallback((pickerType: 'start' | 'end' | 'publish') => {
    confirmDateSelection(pickerType, handleDateSelection);
  }, [confirmDateSelection, handleDateSelection]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <BulletinDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <PhotoUploadSection
        title="Bulletin Photos"
        subtitle="Add up to 10 photos to your bulletin (JPEG, PNG, GIF, WebP)"
        photos={formData.photos || []}
        onPhotosChange={(photos) => handleFieldChange('photos', photos)}
        error={errors.photos}
        maxPhotos={10}
        photoType="bulletin"
      />

      <RecipientOptionsSection
        formData={formData}
        onFieldChange={handleFieldChange}
      />

      <UrgencyOptionsSection
        formData={formData}
        onFieldChange={handleFieldChange}
      />

      <PublishOptionsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onPublishTypeChange={handlePublishTypeChange}
        onPublishTimePress={handlePublishTimePress}
      />
    </>
  ), [
    formData,
    errors,
    handleFieldChange,
    handlePublishTypeChange,
    handlePublishTimePress,
  ]);

  if (layoutMode === 'desktop') {
    return (
      <>
        <DesktopFormPage
          title="Create Bulletin"
          subtitle="Add bulletin details"
          isSubmitting={isLoading}
          submitText={isLoading ? 'Creating...' : 'Create'}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        >
          {scrollContent}
        </DesktopFormPage>
        <BulletinDateTimePickers
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirm as any}
          onCancel={hideDatePickers}
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
        />
      </>
    );
  }

  return (
    <MobileFormScreen
      screenName="CreateBulletin"
      navigation={navigation}
      isSubmitting={isLoading}
      submitText={isLoading ? 'Creating...' : 'Create'}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    >
      {scrollContent}
      <DateTimePickerModal
        DateTimePicker={DateTimePicker}
        datePickerState={datePickerState}
        onDateChange={handleDatePickerChange}
        onConfirm={handleDatePickerConfirm}
        onCancel={hideDatePickers}
        pickerType="publish"
        title="Publish Time"
        onModeChange={setDatePickerMode}
        webAnchor={webAnchor}
      />
    </MobileFormScreen>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 20,
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100, // Space for action buttons
  },
});
